import React, { useEffect, useMemo } from 'react';
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { basemapOptions } from '../map/basemapOptions';
import GoogleTileLayer from '../map/GoogleTileLayer';
import { useGoogleBasemap } from '../../services/googleTiles/useGoogleBasemap';

/**
 * A small, self-contained Leaflet map showing one route in isolation on the basemap — a
 * companion to the timetable grid. Hovering a stop row in the grid highlights that stop
 * here (and gently pans it into view), so the rider can see where each timetable row sits
 * on the line while scrolling. Coordinates are Leaflet `[lat, lng]` (matching the network
 * `RouteShape.coordinates` and `FixedRoute.geometry.coordinates` conventions).
 */

export interface MiniMapStop {
  stopId: string;
  name?: string | null;
  lat: number;
  lng: number;
}

interface RouteMiniMapProps {
  /** Route line in `[lat, lng]`. If empty, the stops are connected in order instead. */
  coordinates: Array<[number, number]>;
  stops: MiniMapStop[];
  routeColor?: string;
  focusedStopId?: string | null;
  onFocusStop?: (stopId: string | null) => void;
  height?: number;
}

/** Fit the map to the route once its geometry is known / changes. */
function FitToRoute({ points }: { points: Array<[number, number]> }): null {
  const map = useMap();
  const key = points.length ? `${points.length}:${points[0].join(',')}:${points[points.length - 1].join(',')}` : '';
  useEffect(() => {
    if (points.length === 0) return;
    // The map can mount inside an animating Collapse with a stale size — re-measure first.
    map.invalidateSize();
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [16, 16], maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

function RouteMiniMap({
  coordinates,
  stops,
  routeColor,
  focusedStopId,
  onFocusStop,
  height = 200,
}: RouteMiniMapProps): React.ReactElement {
  const color = routeColor || '#1976d2';

  // Prefer the real route shape; fall back to connecting stops in order.
  const line = useMemo<Array<[number, number]>>(() => {
    if (coordinates.length >= 2) return coordinates;
    return stops.map((s) => [s.lat, s.lng] as [number, number]);
  }, [coordinates, stops]);

  const allPoints = useMemo<Array<[number, number]>>(
    () => (line.length ? line : stops.map((s) => [s.lat, s.lng] as [number, number])),
    [line, stops],
  );

  const center = allPoints[0] ?? [54.0, -2.8]; // fallback ~ NW England (route 600 country)
  const base = basemapOptions.aesthetic;
  const googleBasemap = useGoogleBasemap();

  return (
    <MapContainer
      center={center}
      zoom={11}
      style={{ height, width: '100%', borderRadius: 8 }}
      scrollWheelZoom={false}
    >
      {googleBasemap.apiKey ? <GoogleTileLayer apiKey={googleBasemap.apiKey} mapType="roadmap" /> : <TileLayer url={base.url()} attribution={base.attribution} />}
      {line.length >= 2 && <Polyline positions={line} pathOptions={{ color, weight: 4, opacity: 0.85 }} />}
      {stops.map((s) => {
        const isFocused = s.stopId === focusedStopId;
        return (
          <CircleMarker
            key={s.stopId}
            center={[s.lat, s.lng]}
            radius={isFocused ? 7 : 4}
            pathOptions={{
              color: isFocused ? '#fff' : color,
              weight: isFocused ? 2 : 1,
              fillColor: color,
              fillOpacity: isFocused ? 1 : 0.7,
            }}
            eventHandlers={{ click: () => onFocusStop?.(s.stopId) }}
          >
            {isFocused && s.name && (
              <Tooltip permanent direction="top" offset={[0, -6]}>
                {s.name}
              </Tooltip>
            )}
          </CircleMarker>
        );
      })}
      <FitToRoute points={allPoints} />
    </MapContainer>
  );
}

export default RouteMiniMap;
